/**
 * Phase 10 R3 — Pure Job Tech-Pay Computation Helper
 *
 * ⚠️  CONCEPT BOUNDARY (do not collapse):
 *
 *   Labor sell ≠ technician pay ≠ labor cost.
 *
 * This module computes only the **technician pay** for a single job line. It
 * is consumed exclusively by the Phase 10 R3 tech-pay module read/write path.
 *
 * ⚠️  `tech_pay_minutes` IS A PAY-SIDE BASIS — IT IS NOT:
 *   • `predicted_minutes` (Book/Job-Catalog minutes — scheduler authority)
 *   • `sold_minutes`      (sell-side R2 snapshot)
 *   • `actual_minutes`    (timer / clock-in elapsed time)
 *   • `opti_time` / `opti_finish` (Wall time / Wall finish — operational)
 *
 * Canonical R3 rule:
 *   tech_pay_minutes = max(referenceMinutes, retailSoldMinutes ?? 0)
 *
 * `referenceMinutes` proxy note:
 *   In R3, `referenceMinutes` is sourced from `ticket_jobs.predicted_minutes`
 *   as the current **reference-time proxy** for pay computation. This is an
 *   implementation bridge, not a redefinition of reference time. If a distinct
 *   canonical `reference_minutes` field is introduced later, the tech-pay
 *   engine MUST migrate to that field. The pay engine is the only consumer of
 *   this proxy — scheduler, sell-side labor (R2), and any future cost/GP
 *   engine continue to read `predicted_minutes` under their own semantics.
 *
 * Hourly safety:
 *   Hourly pay is NOT computed per job. We refuse to fake per-job hourly
 *   payroll precision and surface it honestly as `deferred_hourly`.
 */

export type TechPayMode = 'flat_rate' | 'hourly';
export type TechPayStatus = 'computed' | 'deferred_hourly' | 'no_config';

export interface TechPayConfig {
  pay_mode: TechPayMode;
  /** $/hr for hourly mode (display only — per-job pay deferred in R3). */
  hourly_rate: number | null;
  /** $/flag-hour for flat_rate mode. */
  flat_rate_per_hour: number | null;
}

export interface ComputeJobTechPayInput {
  /** Reference-time proxy (R3: ticket_jobs.predicted_minutes). */
  referenceMinutes: number;
  /** Sell-side snapshot (R2: ticket_jobs.sold_minutes). May be null/undefined. */
  retailSoldMinutes: number | null | undefined;
  /** Resolved pay config for the assigned technician, or null when no row exists. */
  techPayConfig: TechPayConfig | null;
}

export interface ComputedJobTechPay {
  tech_pay_mode: TechPayMode | null;
  tech_pay_minutes: number;
  tech_pay_hours: number;
  tech_pay_rate_applied: number | null;
  tech_pay_amount: number | null;
  tech_pay_reason: string;
  tech_pay_status: TechPayStatus;
}

/** Round dollars to 2dp. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function fmtHrs(mins: number): string {
  return (mins / 60).toFixed(2);
}

export function computeJobTechPay(input: ComputeJobTechPayInput): ComputedJobTechPay {
  const ref = Math.max(0, Math.round(input.referenceMinutes || 0));
  const sold = Math.max(0, Math.round(input.retailSoldMinutes ?? 0));
  const pay_minutes = Math.max(ref, sold);
  const pay_hours = pay_minutes / 60;

  // ── No pay config row for the assigned technician ─────────────────────
  if (!input.techPayConfig) {
    return {
      tech_pay_mode: null,
      tech_pay_minutes: pay_minutes,
      tech_pay_hours: pay_hours,
      tech_pay_rate_applied: null,
      tech_pay_amount: null,
      tech_pay_reason: 'No pay config for assigned technician',
      tech_pay_status: 'no_config',
    };
  }

  const cfg = input.techPayConfig;

  // ── Flat-rate: compute $ per job ──────────────────────────────────────
  if (cfg.pay_mode === 'flat_rate' && (cfg.flat_rate_per_hour ?? 0) > 0) {
    const rate = Number(cfg.flat_rate_per_hour);
    const amount = round2(pay_hours * rate);
    return {
      tech_pay_mode: 'flat_rate',
      tech_pay_minutes: pay_minutes,
      tech_pay_hours: pay_hours,
      tech_pay_rate_applied: round2(rate),
      tech_pay_amount: amount,
      tech_pay_reason:
        `Flat-rate · pay time = max(ref ${fmtHrs(ref)}h, sold ${fmtHrs(sold)}h) ` +
        `= ${fmtHrs(pay_minutes)}h × $${rate.toFixed(2)}/hr`,
      tech_pay_status: 'computed',
    };
  }

  // ── Flat-rate but missing rate value → no_config (still honest) ───────
  if (cfg.pay_mode === 'flat_rate') {
    return {
      tech_pay_mode: 'flat_rate',
      tech_pay_minutes: pay_minutes,
      tech_pay_hours: pay_hours,
      tech_pay_rate_applied: null,
      tech_pay_amount: null,
      tech_pay_reason: 'Flat-rate mode selected but no flat-rate per hour configured',
      tech_pay_status: 'no_config',
    };
  }

  // ── Hourly: honest deferral. NEVER compute fake $ per job. ────────────
  return {
    tech_pay_mode: 'hourly',
    tech_pay_minutes: pay_minutes,
    tech_pay_hours: pay_hours,
    tech_pay_rate_applied: null,
    tech_pay_amount: null,
    tech_pay_reason:
      'Hourly pay mode — per-job pay not computed in R3 (requires actual-time allocation)',
    tech_pay_status: 'deferred_hourly',
  };
}

/**
 * Build the partial ticket_jobs payload for a tech-pay write.
 * Used by every persistence call site to keep column shape consistent.
 */
export function techPayColumnsForUpsert(c: ComputedJobTechPay) {
  return {
    tech_pay_mode: c.tech_pay_mode,
    tech_pay_minutes: c.tech_pay_minutes,
    tech_pay_rate_applied: c.tech_pay_rate_applied,
    tech_pay_amount: c.tech_pay_amount,
    tech_pay_reason: c.tech_pay_reason,
    tech_pay_status: c.tech_pay_status,
  };
}
